// Rendu HTML d'un CV à partir du profil + settings.
//
// Pourquoi HTML/CSS plutôt que jsPDF "à la main" :
//  - vrais glyphes Unicode (accents OK sans hack TTF embarquée) ;
//  - mise en page riche (espacements, hiérarchie typo, filets d'accent) ;
//  - le même rendu sert pour la **prévisualisation** (iframe srcdoc) ET
//    pour l'**export PDF** (via jsPDF.html() / html2canvas) ;
//  - testable par simple inspection de chaîne — pas besoin de rasteriser.
//
// Invariants ATS volontaires (cf. analyse-produit §2.1) :
//  - **mono-colonne** : pas de tableaux, pas de grid à 2 cols ;
//  - ordre fixé : contact → résumé → compétences → expériences → formation
//    → langues → projets ;
//  - police 10–12 pt, sans icône ni barre de progression ;
//  - le template "Moderne" reste mono-colonne, n'ajoute qu'un filet de couleur
//    et une typo soignée — rien qui casse un parser.
//
// Le catalogue de Radar (issue #235) ajoute trois modèles : « Dates à gauche »
// et « Compact » restent mono-colonne ; « Deux colonnes » est le seul à
// déroger, et Radar le dit sous sa vignette. L'extension et la console ne
// proposent toujours que ATS et Moderne : le catalogue est une liste que
// l'hôte passe à `PreviewModal`, pas un changement des modèles existants.
// « Ma mise en page » (`Perso`) n'est pas rendu ici mais par `miseEnPage.ts`.

import type { ResumeProfile } from './schema'
import { buildRevisionBridgeScript } from './revisionBridge'
import { documentBaseName } from './documentName'
import { toResumeLink, type ResumeLink } from './links'
import { encadrerMiseEnPage, remplirModele, validerMiseEnPage } from './miseEnPage'

export type ResumeTemplate = 'ATS-Optimized' | 'Moderne' | 'Dates' | 'Colonnes' | 'Compact' | 'Perso'

export const RESUME_TEMPLATES: readonly ResumeTemplate[] = ['ATS-Optimized', 'Moderne', 'Dates', 'Colonnes', 'Compact', 'Perso'] as const

/** Les modèles qui colorent leurs titres. ATS reste noir, quelle que soit
 *  la couleur choisie : c'est ce qui le distingue de Moderne. */
export const colore = (t: ResumeTemplate): boolean => t !== 'ATS-Optimized'

// Marge de page (mm), appliquée sur les 4 côtés. SOURCE UNIQUE : utilisée à la
// fois par `@page` (impression) et par le script d'aperçu des sauts de page, qui
// doivent rester synchronisés pour que la preview reflète exactement le PDF.
// Le modèle Compact en prend une plus étroite : le document la porte
// (`data-marge`), et c'est elle que `paginatePreview` relit.
const PAGE_MARGIN_MM = 16
const MARGE_COMPACT_MM = 12
const margeDe = (t: ResumeTemplate): number => (t === 'Compact' ? MARGE_COMPACT_MM : PAGE_MARGIN_MM)

/** Sections optionnelles qu'on peut masquer depuis le panneau live (B1).
 *  Seules l'en-tête (contact) et les expériences sont toujours affichées ;
 *  tout le reste est masquable. Absent / non-false => visible. */
export interface SectionVisibility {
  summary?: boolean
  skills?: boolean
  education?: boolean
  languages?: boolean
  projects?: boolean
}

/** Locale des TITRES de section (le contenu, lui, vient déjà traduit de l'IA). */
export type ResumeLocale = 'fr' | 'en' | 'nl' | 'de'

/**
 * En-tête de la lettre de motivation.
 *
 * `none` — la lettre s'ouvre sur la formule d'appel. Sobre, et jamais faux.
 * `sender` — bloc expéditeur d'une vraie lettre (nom, ville, e-mail, téléphone),
 *            en corps de texte, aligné à gauche.
 * `name` — l'ancien gros titre coloré. Conservé pour qui l'aime, jamais le défaut :
 *          personne n'écrit son propre nom en titre d'une lettre, et il figure
 *          déjà en signature deux lignes plus bas.
 *
 * Le choix est laissé à l'utilisateur parce que c'est un goût et une convention
 * qui varie d'un pays à l'autre — pas quelque chose qui se tranche dans le code.
 */
export type LetterHeader = 'none' | 'sender' | 'name'

export const LETTER_HEADERS: readonly LetterHeader[] = ['none', 'sender', 'name'] as const

/** Coordonnées de l'expéditeur (bloc `sender`). Tout est optionnel : une ligne
 *  absente est omise, jamais remplacée par un trou. */
export interface SenderIdentity {
  name?: string
  email?: string
  phone?: string
  location?: string
}

/** Titres de section localisés. Le résumé n'a pas de titre visible. */
export const SECTION_TITLES: Record<ResumeLocale, { skills: string; work: string; education: string; languages: string; projects: string }> = {
  fr: { skills: 'Compétences', work: 'Expériences', education: 'Formation', languages: 'Langues', projects: 'Projets' },
  en: { skills: 'Skills', work: 'Experience', education: 'Education', languages: 'Languages', projects: 'Projects' },
  nl: { skills: 'Vaardigheden', work: 'Werkervaring', education: 'Opleiding', languages: 'Talen', projects: 'Projecten' },
  de: { skills: 'Kompetenzen', work: 'Berufserfahrung', education: 'Ausbildung', languages: 'Sprachen', projects: 'Projekte' },
}

/** Fin d'une expérience en cours, dans la langue du document. Codée en dur en
 *  français jusqu'au 2026-10-05 : un CV anglais affichait « Nov 2024 – Aujourd'hui ». */
export const ONGOING_LABEL: Record<ResumeLocale, string> = {
  fr: "Aujourd'hui",
  en: 'Present',
  nl: 'Heden',
  de: 'Heute',
}

/** Mappe un libellé de langue (valeurs de `resolveOutputLanguage`) vers une
 *  locale de titres. Défaut : français (rétro-compatibilité). */
function localeFromLanguage(label?: string): ResumeLocale {
  switch (label) {
    case 'Anglais': case 'English': return 'en'
    case 'Néerlandais': case 'Nederlands': case 'Dutch': return 'nl'
    case 'Allemand': case 'Deutsch': case 'German': return 'de'
    default: return 'fr'
  }
}

// Palette d'accents pré-définie (1 click = 1 couleur). Partagée entre la page
// Options et le panneau live de la preview (B1). 6 teintes sobres, ATS-safe.
export const ACCENT_PALETTE: Array<{ hex: string; label: string }> = [
  { hex: '0a66c2', label: 'Bleu' },
  { hex: '1f4e79', label: 'Bleu marine' },
  { hex: '2e7d32', label: 'Vert sapin' },
  { hex: '6a1b9a', label: 'Violet' },
  { hex: 'b71c1c', label: 'Bordeaux' },
  { hex: '424242', label: 'Gris ardoise' },
]

/** Le noir de Radar (issue #235) : son catalogue colore tous ses modèles, et
 *  « Sobre » en noir y tient la place de l'ancien ATS. Hors de la palette
 *  partagée, que l'extension garde telle quelle. */
export const ACCENT_NOIR = { hex: '1a1a1a', label: 'Noir' }

export interface RenderSettings {
  template?: ResumeTemplate
  /** Couleur d'accent en hex (6 chars, sans #). Défaut : bleu. */
  accentColor?: string
  /** Inclure une photo (option ATS-désactivée par défaut). */
  showPhoto?: boolean
  /** Data URL de la photo (data:image/...). Ignoré si showPhoto = false. */
  photoDataUrl?: string
  /** Rend le CV éditable inline (contenteditable) dans la preview. §2.4 */
  editable?: boolean
  /** Sections optionnelles à afficher/masquer (B1). Défaut : toutes visibles. */
  sections?: SectionVisibility
  /** Langue de sortie RÉSOLUE (libellé, ex. 'Anglais') — pilote les TITRES de
   *  section. Absent => titres en français (rétro-compatibilité). */
  outputLanguage?: string
  /** En-tête de la LETTRE (sans effet sur le CV). `none` par défaut : une lettre
   *  qui commence par la formule d'appel n'est jamais fautive, alors que le nom
   *  du candidat en gros titre l'est presque toujours. `sender` pose le bloc
   *  expéditeur d'une vraie lettre ; `name` garde l'ancien titre pour qui l'aime. */
  letterHeader?: LetterHeader
  /** Coordonnées de l'expéditeur, pour l'en-tête `sender`. Elles vivent dans le
   *  PROFIL, pas dans la lettre : `CoverLetter` est ce que produit l'IA, on ne
   *  l'élargit pas pour y loger des données qu'elle n'écrit pas. */
  sender?: SenderIdentity
  /** Employeur de l'offre visée. Sert UNIQUEMENT à nommer le document : c'est
   *  le `<title>` de cette page que Chrome propose comme nom de PDF à
   *  l'impression. Absent => segment omis (`nicolas_breels_cv`), jamais deviné. */
  company?: string
  /** Le modèle HTML de « Ma mise en page » (issue #235), lu par
   *  `renderResumeToHTML` quand `template` vaut `Perso`. Absent ou refusé par
   *  `validerMiseEnPage`, le CV retombe sur Moderne : jamais une page vide. */
  miseEnPage?: string
}

export interface ResolvedSettings {
  template: ResumeTemplate
  miseEnPage: string
  accentColor: string
  showPhoto: boolean
  photoDataUrl: string
  editable: boolean
  sections: Required<SectionVisibility>
  locale: ResumeLocale
  company: string
  letterHeader: LetterHeader
  sender: SenderIdentity
}

// Validation/normalisation des settings. Lecture défensive pour ne jamais planter
// sur des entrées tordues (settings sauvegardés d'une ancienne version, etc.).
export function resolveRenderSettings(s: unknown): ResolvedSettings {
  const o = (s ?? {}) as Record<string, unknown>
  const tpl: ResumeTemplate = RESUME_TEMPLATES.includes(o.template as ResumeTemplate) ? (o.template as ResumeTemplate) : 'ATS-Optimized'
  const miseEnPage = typeof o.miseEnPage === 'string' ? o.miseEnPage : ''
  const accent = typeof o.accentColor === 'string' && /^[0-9a-f]{6}$/i.test(o.accentColor)
    ? o.accentColor.toLowerCase()
    : '0a66c2'
  const showPhoto = o.showPhoto === true
  const photoDataUrl = typeof o.photoDataUrl === 'string' && o.photoDataUrl.startsWith('data:image/')
    ? o.photoDataUrl
    : ''
  const editable = o.editable === true
  // Sections : visibles par défaut ; seul `false` explicite masque.
  const sec = (o.sections ?? {}) as Record<string, unknown>
  const sections = {
    summary: sec.summary !== false,
    skills: sec.skills !== false,
    education: sec.education !== false,
    languages: sec.languages !== false,
    projects: sec.projects !== false,
  }
  const locale = localeFromLanguage(typeof o.outputLanguage === 'string' ? o.outputLanguage : undefined)
  const company = typeof o.company === 'string' ? o.company : ''
  // En-tête de lettre : seule une valeur connue est retenue. Un réglage enregistré
  // par une version future (ou tordu) retombe sur `none`, qui n'est jamais faux.
  const letterHeader = LETTER_HEADERS.includes(o.letterHeader as LetterHeader)
    ? (o.letterHeader as LetterHeader)
    : 'none'
  const exp = (o.sender ?? {}) as Record<string, unknown>
  const texte = (v: unknown) => (typeof v === 'string' ? v.trim() : '')
  const sender: SenderIdentity = {
    name: texte(exp.name),
    email: texte(exp.email),
    phone: texte(exp.phone),
    location: texte(exp.location),
  }
  return { template: tpl, miseEnPage, accentColor: accent, showPhoto, photoDataUrl, editable, sections, locale, company, letterHeader, sender }
}

// --- Marquage des zones éditables ----------------------------------------
//
// En mode `editable` UNIQUEMENT, chaque zone dont le texte peut être RELU et
// réécrit dans le profil porte un `data-field` : son chemin dans le JSON.
// C'est le contrat que lit `harvestResume` (resumeHarvest.ts).
//
// Corollaire tenu ici, et à tenir en ajoutant un champ : ce qui n'est pas
// marqué est rendu `contenteditable="false"`. Un champ éditable mais non
// récolté serait le pire des deux mondes — on le modifie, ça s'affiche, et ça
// disparaît sans un mot au premier enregistrement. C'est exactement ce que
// faisait toute la preview avant l'ajout de la récolte : l'export repart du
// JSON, jamais du DOM.
//
// Rien de tout ça n'est émis hors mode éditable : l'HTML d'export, d'impression
// et de diff reste identique au caractère près.
const field = (cfg: ResolvedSettings, path: string): string =>
  cfg.editable ? ` data-field="${path}"` : ''

/** Marque un CONTENEUR d'élément de liste (une expérience, une formation…).
 *  Sa disparition du DOM = suppression de l'entrée à la récolte. */
const itemAttr = (cfg: ResolvedSettings, path: string): string =>
  cfg.editable ? ` data-item="${path}"` : ''

/** Zone affichée mais non récoltable (titres de section, périodes). */
const locked = (cfg: ResolvedSettings): string =>
  cfg.editable ? ' contenteditable="false"' : ''

// --- Helpers HTML ---------------------------------------------------------

/**
 * Échappe un texte pour insertion sûre dans du HTML.
 * Usage : tout texte qui vient du profil utilisateur.
 *
 * On gère les 5 caractères critiques + apostrophe. Pas besoin d'une lib :
 * le surface d'attaque est minimal (le profil est local) mais la robustesse
 * vaut mieux qu'un bug d'affichage si le user a un `<` dans une description.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Affiche la période. Pour une expérience en cours (endDate vide) on passe
// `ongoingLabel` = ONGOING_LABEL[locale] : début présent + fin vide -> "Jan 2020 – Present".
// Sans ongoingLabel (ex. formation), une fin vide reste simplement omise.
const periodOf = (item: { startDate?: string; endDate?: string }, ongoingLabel?: string): string => {
  const start = item.startDate?.trim() || ''
  const end = item.endDate?.trim() || ''
  if (start && !end && ongoingLabel) return `${start} – ${ongoingLabel}`
  return [start, end].filter(Boolean).join(' – ')
}

// Mois FR + EN (noms complets et abréviations) -> numéro 1-12, pour trier des
// dates en texte libre ("Jan 2020", "mars 2019", "September 2021", "03/2021"…).
const MONTHS: Record<string, number> = {
  janvier: 1, january: 1, jan: 1, janv: 1,
  février: 2, fevrier: 2, february: 2, feb: 2, fev: 2, fév: 2, févr: 2,
  mars: 3, march: 3, mar: 3,
  avril: 4, april: 4, avr: 4, apr: 4,
  mai: 5, may: 5,
  juin: 6, june: 6, jun: 6,
  juillet: 7, july: 7, juil: 7, jul: 7,
  août: 8, aout: 8, august: 8, aug: 8, aoû: 8,
  septembre: 9, september: 9, sept: 9, sep: 9,
  octobre: 10, october: 10, oct: 10,
  novembre: 11, november: 11, nov: 11,
  décembre: 12, decembre: 12, december: 12, déc: 12, dec: 12,
}
// Entrées triées par longueur de nom décroissante : "janvier" testé avant "jan".
const MONTH_ENTRIES = Object.entries(MONTHS).sort((a, b) => b[0].length - a[0].length)

// Convertit une date texte en rang comparable (année*12 + mois). Sans année -> 0.
function dateRank(raw: string): number {
  const s = (raw || '').toLowerCase()
  const year = s.match(/\d{4}/)
  if (!year) return 0
  const rest = s.replace(year[0], ' ')
  let month = 0
  for (const [name, n] of MONTH_ENTRIES) {
    if (rest.includes(name)) { month = n; break }
  }
  if (!month) {
    const mn = rest.match(/(?:^|[^\d])(0?[1-9]|1[0-2])(?:[^\d]|$)/)
    if (mn) month = parseInt(mn[1], 10)
  }
  return parseInt(year[0], 10) * 12 + month
}

// Tri anté-chronologique : poste en cours (endDate vide) en haut, puis du plus
// récent au plus ancien. Stable -> à fin égale, l'ordre saisi (boutons monter/
// descendre de l'éditeur) est conservé.
function sortWorkRecentFirst<T extends { startDate?: string; endDate?: string }>(work: readonly T[]): T[] {
  const endRank = (w: T) => (w.endDate?.trim() ? dateRank(w.endDate) : Number.POSITIVE_INFINITY)
  return [...work].sort((a, b) => (endRank(b) - endRank(a)) || (dateRank(b.startDate || '') - dateRank(a.startDate || '')))
}

// Rend une liste de lignes en bullet-list HTML, en filtrant le vide.
//
// La marque porte sur le `<ul>`, pas sur chaque `<li>` : la récolte relit les
// puces dans l'ordre du DOM. Un index par puce se serait décalé au premier
// ajout ou à la première suppression — là, les trois gestes (ajouter, retirer,
// réordonner) marchent sans code de plus.
function renderBullets(items: string[], cfg: ResolvedSettings, path: string): string {
  const cleaned = items.map((s) => (s ?? '').trim()).filter(Boolean)
  if (cleaned.length === 0) return ''
  const mark = cfg.editable ? `${field(cfg, path)} data-list="lines"` : ''
  return `<ul class="bullets"${mark}>${cleaned.map((h) => `<li>${escapeHtml(h)}</li>`).join('')}</ul>`
}

/**
 * Une ancre. C'est tout ce qu'il faut pour un lien cliquable dans le PDF :
 * Chrome garde les `<a href>` en imprimant l'aperçu.
 *
 * `target="_blank"` n'est pas là pour le PDF mais pour l'aperçu : sans lui, un
 * clic remplacerait le CV par le site dans l'iframe. Comme l'iframe est
 * sandboxée sans `allow-popups`, le clic n'y fait alors simplement rien.
 */
function renderLink(link: ResumeLink, cfg: ResolvedSettings, cls = 'link'): string {
  return `<a class="${cls}" href="${escapeHtml(link.href)}" target="_blank" rel="noopener noreferrer"${locked(cfg)}>${escapeHtml(link.text)}</a>`
}

// --- Sections -------------------------------------------------------------

function renderHeader(cv: ResumeProfile, cfg: ResolvedSettings): string {
  // Chaque coordonnée devient sa propre zone en mode éditable : la ligne
  // « email · téléphone · ville » n'est pas re-découpable de façon fiable une
  // fois aplatie (une ville peut contenir le séparateur).
  const contactParts = ([
    ['email', cv.basics.email],
    ['phone', cv.basics.phone],
    ['location', cv.basics.location],
  ] as const).filter(([, v]) => Boolean(v))
  // Les liens suivent les coordonnées, sur la même ligne. Ils ne sont pas
  // récoltables (l'URL se corrige dans l'éditeur de profil, pas dans le
  // texte raccourci qu'on affiche) : `locked` les sort de la zone éditable.
  const liens = cv.basics.profiles
    .map((p) => toResumeLink(p.url))
    .filter((l): l is ResumeLink => l !== null)
  const contact = [
    ...contactParts.map(([k, v]) => (cfg.editable ? `<span${field(cfg, `basics.${k}`)}>${escapeHtml(v)}</span>` : escapeHtml(v))),
    ...liens.map((l) => renderLink(l, cfg)),
  ].join(' · ')
  const photo = cfg.showPhoto && cfg.photoDataUrl
    ? `<img class="photo" src="${cfg.photoDataUrl}" alt="" />`
    : ''
  // L'ordre `name → label → contact` est figé : c'est ce que les ATS attendent.
  return `
    <header class="resume-header">
      ${photo}
      <div class="header-text">
        <h1 class="name"${field(cfg, 'basics.name')}>${escapeHtml(cv.basics.name || 'Nom')}</h1>
        ${cv.basics.label ? `<p class="label"${field(cfg, 'basics.label')}>${escapeHtml(cv.basics.label)}</p>` : ''}
        ${contact ? `<p class="contact">${contact}</p>` : ''}
      </div>
    </header>`
}

function renderSummary(cv: ResumeProfile, cfg: ResolvedSettings): string {
  if (!cv.basics.summary) return ''
  return `<section class="summary"><p${field(cfg, 'basics.summary')}>${escapeHtml(cv.basics.summary)}</p></section>`
}

type SectionTitles = (typeof SECTION_TITLES)[ResumeLocale]

function renderSkills(cv: ResumeProfile, titles: SectionTitles, cfg: ResolvedSettings): string {
  if (!cv.skills.length) return ''
  // Liste séparée par virgules : 100% parsable ATS, pas de tags graphiques
  // (les bulles colorées sont jolies mais cassent souvent le parsing).
  const mark = cfg.editable ? `${field(cfg, 'skills')} data-list="comma"` : ''
  return `
    <section class="section">
      <h2 class="section-title"${locked(cfg)}>${titles.skills}</h2>
      <p class="skills"${mark}>${cv.skills.map(escapeHtml).join(', ')}</p>
    </section>`
}

function renderWork(cv: ResumeProfile, titles: SectionTitles, cfg: ResolvedSettings): string {
  if (!cv.work.length) return ''
  // L'affichage est anté-chronologique, le JSON garde son ordre : les chemins
  // marqués doivent donc porter l'index D'ORIGINE, pas celui de la ligne à
  // l'écran. Sans ça, la récolte réécrirait la bonne phrase dans la mauvaise
  // expérience dès que l'ordre affiché diffère de l'ordre stocké.
  const indexed = cv.work.map((exp, i) => ({ ...exp, __index: i }))
  const items = sortWorkRecentFirst(indexed).map((exp) => {
    const i = exp.__index
    const titleParts = ([['position', exp.position], ['name', exp.name]] as const).filter(([, v]) => Boolean(v))
    const head = titleParts
      .map(([k, v]) => (cfg.editable ? `<span${field(cfg, `work.${i}.${k}`)}>${escapeHtml(v)}</span>` : escapeHtml(v)))
      .join(' · ')
    const period = periodOf(exp, ONGOING_LABEL[cfg.locale])
    const summaryEsc = exp.summary
      ? `<p class="exp-summary"${field(cfg, `work.${i}.summary`)}>${escapeHtml(exp.summary)}</p>`
      : ''
    return `
      <article class="exp-item"${itemAttr(cfg, `work.${i}`)}>
        <div class="exp-head">
          <span class="exp-title">${head}</span>
          ${period ? `<span class="exp-period"${locked(cfg)}>${escapeHtml(period)}</span>` : ''}
        </div>
        ${summaryEsc}
        ${renderBullets(exp.highlights, cfg, `work.${i}.highlights`)}
      </article>`
  }).join('')
  return `
    <section class="section">
      <h2 class="section-title"${locked(cfg)}>${titles.work}</h2>
      ${items}
    </section>`
}

function renderEducation(cv: ResumeProfile, titles: SectionTitles, cfg: ResolvedSettings): string {
  if (!cv.education.length) return ''
  const items = cv.education.map((edu, i) => {
    const leftParts = ([['studyType', edu.studyType], ['area', edu.area]] as const).filter(([, v]) => Boolean(v))
    const left = leftParts
      .map(([k, v]) => (cfg.editable ? `<span${field(cfg, `education.${i}.${k}`)}>${escapeHtml(v)}</span>` : escapeHtml(v)))
      .join(', ')
    const inst = edu.institution
      ? (cfg.editable ? `<span${field(cfg, `education.${i}.institution`)}>${escapeHtml(edu.institution)}</span>` : escapeHtml(edu.institution))
      : ''
    const per = periodOf(edu)
    const right = [inst, per ? `<span${locked(cfg)}>${escapeHtml(per)}</span>` : ''].filter(Boolean).join(' · ')
    return `
      <div class="edu-item"${itemAttr(cfg, `education.${i}`)}>
        <span class="edu-left">${left}</span>
        ${right ? `<span class="edu-right">${right}</span>` : ''}
      </div>`
  }).join('')
  return `
    <section class="section">
      <h2 class="section-title"${locked(cfg)}>${titles.education}</h2>
      ${items}
    </section>`
}

function renderLanguages(cv: ResumeProfile, titles: SectionTitles, cfg: ResolvedSettings): string {
  if (!cv.languages.length) return ''
  const list = cv.languages
    .map((l) => l.fluency ? `${escapeHtml(l.language)} (${escapeHtml(l.fluency)})` : escapeHtml(l.language))
    .join(', ')
  const mark = cfg.editable ? `${field(cfg, 'languages')} data-list="langs"` : ''
  return `
    <section class="section">
      <h2 class="section-title"${locked(cfg)}>${titles.languages}</h2>
      <p${mark}>${list}</p>
    </section>`
}

/** Le lien d'un projet, à droite de son titre comme la période d'une
 *  expérience. À part du titre : celui-ci reste une zone éditable, une ancre
 *  autour l'aurait rendu cliquable pendant qu'on le corrige. */
function projectLink(url: string | undefined, cfg: ResolvedSettings): string {
  const link = toResumeLink(url)
  return link ? renderLink(link, cfg, 'link proj-link') : ''
}

function renderProjects(cv: ResumeProfile, titles: SectionTitles, cfg: ResolvedSettings): string {
  if (!cv.projects.length) return ''
  const items = cv.projects.map((p, i) => `
    <article class="proj-item"${itemAttr(cfg, `projects.${i}`)}>
      <div class="proj-head"><span class="proj-title"${field(cfg, `projects.${i}.name`)}>${escapeHtml(p.name)}</span>${projectLink(p.url, cfg)}</div>
      ${p.description ? `<p class="proj-desc"${field(cfg, `projects.${i}.description`)}>${escapeHtml(p.description)}</p>` : ''}
      ${renderBullets(p.highlights, cfg, `projects.${i}.highlights`)}
    </article>`).join('')
  return `
    <section class="section">
      <h2 class="section-title"${locked(cfg)}>${titles.projects}</h2>
      ${items}
    </section>`
}

// --- CSS ------------------------------------------------------------------

/**
 * Génère la feuille de style en fonction du template + couleur d'accent.
 * Volontairement inline (pas de lien externe) pour fonctionner aussi bien
 * dans un iframe srcdoc que dans une conversion PDF (pas de fetch externe).
 *
 * Variables CSS pour permettre de changer la couleur d'accent côté UI sans
 * regénérer tout le HTML.
 */
export function buildCss(cfg: ResolvedSettings): string {
  const isModern = colore(cfg.template)
  const accent = `#${cfg.accentColor}`
  const marge = margeDe(cfg.template)

  // Base commune : police système (lisible partout), mise en page mono-colonne,
  // tailles 10–12 pt comme l'ATS l'attend.
  const base = `
    :root { --accent: ${accent}; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      font-family: 'Helvetica Neue', Arial, 'Liberation Sans', sans-serif;
      font-size: 11pt;
      line-height: 1.45;
      color: #1a1a1a;
      background: #ffffff;
    }
    .resume {
      position: relative;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: ${marge}mm;
      background: #fff;
    }
    .resume-header {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      margin-bottom: 14px;
    }
    .header-text { flex: 1; }
    .photo {
      width: 70px; height: 70px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }
    .name {
      font-size: 22pt;
      font-weight: 700;
      margin: 0 0 2px 0;
      line-height: 1.1;
    }
    .label {
      font-size: 12pt;
      margin: 0 0 4px 0;
      color: #555;
    }
    .contact {
      font-size: 9.5pt;
      color: #666;
      margin: 0;
    }
    .section { margin-top: 14px; }
    .section-title {
      font-size: 11.5pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 0 0 6px 0;
      padding-bottom: 3px;
      border-bottom: 0.5pt solid #ddd;
    }
    .summary p {
      margin: 8px 0 0 0;
      font-style: italic;
      color: #333;
    }
    .exp-item, .proj-item { margin-bottom: 10px; }
    .exp-item:last-child, .proj-item:last-child { margin-bottom: 0; }
    .exp-head, .proj-head, .edu-item {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: baseline;
    }
    .exp-title, .proj-title {
      font-weight: 600;
    }
    .exp-period, .edu-right {
      color: #666;
      font-size: 10pt;
      white-space: nowrap;
    }
    .link { color: inherit; text-decoration: none; }
    .proj-link {
      color: #666;
      font-size: 10pt;
      white-space: nowrap;
    }
    .exp-summary, .proj-desc {
      margin: 2px 0 4px 0;
      color: #444;
    }
    .bullets {
      margin: 4px 0 0 0;
      padding-left: 18px;
    }
    .bullets li { margin-bottom: 2px; }
    .skills { margin: 0; }
    .edu-item { margin-bottom: 4px; }
  `

  // Template Moderne : on ajoute juste un filet d'accent sous le nom et au-dessous
  // des titres de section. **Mono-colonne préservé**, aucune icône, aucune barre.
  const modern = isModern ? `
    .name { color: var(--accent); }
    .label { color: #333; }
    .link { color: var(--accent); }
    .resume-header { border-bottom: 2pt solid var(--accent); padding-bottom: 8px; }
    .section-title {
      color: var(--accent);
      border-bottom-color: var(--accent);
      border-bottom-width: 1pt;
    }
  ` : ''

  // Le catalogue de Radar (issue #235). Chaque modèle ne fait que réarranger
  // les mêmes blocs : les marques `data-field` restent posées pareil, donc la
  // retouche à la main et sa récolte marchent sur les quatre.
  const variante = (() => {
    switch (cfg.template) {
      // La période passe dans une colonne à gauche de chaque expérience.
      // `display: contents` laisse titre et période dans la grille sans
      // changer le HTML que la récolte relit.
      case 'Dates': return `
    .t-dates .exp-item { display: grid; grid-template-columns: 28mm 1fr; column-gap: 5mm; }
    .t-dates .exp-head { display: contents; }
    .t-dates .exp-period { grid-column: 1; grid-row: 1 / span 6; white-space: normal; padding-top: 1px; }
    .t-dates .exp-title, .t-dates .exp-summary, .t-dates .bullets { grid-column: 2; }
    .t-dates .exp-title { display: block; }
  `
      // Une colonne de côté (compétences, langues, formation), le parcours à
      // droite. Le seul modèle à deux colonnes : Radar le dit sous la vignette.
      case 'Colonnes': return `
    .cols { display: grid; grid-template-columns: 56mm 1fr; column-gap: 7mm; }
    .col-cote { border-right: 0.5pt solid #ddd; padding-right: 6mm; }
    .col-cote > .section:first-child, .col-corps > :first-child { margin-top: 0; }
    .col-corps > .summary p { margin-top: 0; }
    .col-cote .edu-item { display: block; margin-bottom: 6px; }
    .col-cote .edu-left { display: block; font-weight: 600; }
    .col-cote .edu-right { display: block; white-space: normal; }
    .col-cote .section-title { font-size: 10.5pt; }
  `
      // Plus serré : police, marges et espacements, pour tenir en une page.
      case 'Compact': return `
    html, body { font-size: 10pt; line-height: 1.35; }
    .name { font-size: 18pt; }
    .label { font-size: 11pt; }
    .resume-header { margin-bottom: 8px; }
    .section { margin-top: 9px; }
    .section-title { font-size: 10.5pt; margin-bottom: 4px; }
    .exp-item, .proj-item { margin-bottom: 6px; }
    .bullets { margin-top: 2px; }
    .bullets li { margin-bottom: 0; }
    .exp-period, .edu-right, .proj-link { font-size: 9pt; }
    .contact { font-size: 9pt; }
  `
      default: return ''
    }
  })()

  // Mode édition (§2.4) : le `.resume` devient éditable inline. On signale les
  // zones focusables sans rien casser à l'impression (cf. @media print plus bas).
  const editable = cfg.editable ? `
    .resume[contenteditable="true"] { outline: none; }
    .resume[contenteditable="true"] :focus {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
      border-radius: 2px;
    }
  ` : ''

  // CSS d'impression (§2.4 — moteur PDF = impression native de Chrome).
  // C'est CE bloc qui garantit "ce que tu vois = le PDF" :
  //  - @page : A4 avec une marge SUR LES 4 CÔTÉS, appliquée à CHAQUE page. C'est
  //    ce qui donne une marge en haut des pages 2+ (sinon le contenu collait au
  //    bord). En impression, `.resume` perd donc son padding/sa largeur max : le
  //    contenu remplit la boîte de contenu de la page (largeur = 210 - 2*marge),
  //    identique à la largeur de la preview écran -> le rendu reste « WYSIWYG » ;
  //  - print-color-adjust: exact -> Chrome imprime bien les filets d'accent ;
  //  - break-inside: avoid sur CHAQUE entrée (exp/proj/edu) -> une expérience n'est
  //    jamais coupée en deux. /!\ PAS sur `.section` : une section entière (ex.
  //    « Expériences ») DOIT pouvoir s'étaler sur 2 pages, sinon elle saute en bloc
  //    à la page suivante et laisse un grand blanc (bug constaté) ;
  //  - break-after: avoid sur les titres -> pas de titre de section orphelin en bas
  //    de page (le titre reste collé à son contenu).
  const print = `
    @page { size: A4; margin: ${marge}mm; }
    @media print {
      html, body { background: #fff; }
      .resume {
        width: auto !important;
        max-width: none;
        min-height: 0 !important;
        margin: 0;
        padding: 0;
        background: #fff !important;
        box-shadow: none;
        transform: none !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .resume :focus { outline: none !important; }
      /* La pagination écran (feuilles, échelle, décalages) ne doit JAMAIS toucher
         le PDF : à l'impression, Chrome pagine nativement via @page. */
      .pv-stage { width: auto !important; height: auto !important; }
      .pv-sheet { display: none !important; }
    }
    .exp-item, .proj-item, .edu-item {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .section-title {
      break-after: avoid;
      page-break-after: avoid;
    }
    .resume {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  `

  // Aperçu paginé « feuilles A4 » (écran UNIQUEMENT — jamais imprimé/exporté).
  //
  // POURQUOI : la cause des bugs de saut de page était que la preview rendait le CV
  // à une largeur variable (l'iframe), donc PAS à la largeur d'une vraie page A4 ->
  // les retours à la ligne et les hauteurs différaient du PDF, et tout repère de
  // coupure tombait à côté. Ici le CV est rendu à 210mm FIXE (cf. `.resume`), donc
  // la mise en page écran est IDENTIQUE au PDF, puis :
  //  - un script (buildPageBreakScript) découpe le flux en pages : il décale chaque
  //    entrée qui déborde vers le haut de la page suivante (`.pb-pushed` + `--pb-gap`)
  //    et dessine une vraie feuille blanche A4 (`.pv-sheet`) par page, sur fond gris ;
  //  - l'ensemble est mis à l'échelle (`--pv-scale`) pour tenir dans l'iframe.
  // En @media print, tout ceci est neutralisé : Chrome pagine nativement via @page.
  const pageBreaks = cfg.editable ? `
    @media screen {
      html, body { background: #e5e7eb; }
      .pv-stage { margin: 0 auto; }
      .resume {
        background: transparent;
        min-height: var(--pv-minh, 297mm);
        transform: scale(var(--pv-scale, 1));
        transform-origin: top left;
      }
      /* Feuilles blanches A4 dessinées DERRIÈRE le contenu (z-index négatif ->
         pas besoin de positionner le contenu, donc offsetTop reste relatif au
         .resume et la pagination reste juste). */
      .pv-sheet {
        position: absolute;
        left: 0;
        width: 210mm;
        height: 297mm;
        background: #fff;
        box-shadow: 0 1px 8px rgba(0,0,0,0.18);
        z-index: -1;
      }
      .pb-pushed { margin-top: var(--pb-gap, 0) !important; }
    }
  ` : ''

  return base + modern + variante + editable + print + pageBreaks
}

// Script d'aperçu paginé « feuilles A4 » (écran only). Le CV est rendu à 210mm fixe
// (donc identique au PDF). Ce script :
//  1. mesure chaque entrée insécable (exp/proj/edu) et, quand elle déborderait de la
//     page courante, la DÉCALE pour qu'elle démarre en haut de la page suivante
//     (`.pb-pushed` + `--pb-gap`), exactement comme `break-inside: avoid` à l'impression ;
//  2. dessine une vraie feuille blanche A4 (`.pv-sheet`) par page, derrière le contenu ;
//  3. met l'ensemble à l'échelle (`--pv-scale`) pour tenir dans la largeur de l'iframe.
// Recalculé au chargement, au resize et pendant l'édition. Tout est @media screen :
// l'impression ignore feuilles/échelle/décalages et pagine nativement via @page.
// Pagine l'aperçu en feuilles A4 (écran). À appeler DEPUIS LE PARENT (content
// script) sur le `contentDocument` de l'iframe.
//
// /!\ POURQUOI côté parent et pas un <script> dans l'iframe : l'iframe d'aperçu est
// sandboxée SANS `allow-scripts` (et la CSP des sites hôtes — LinkedIn/Indeed —
// bloquerait de toute façon un <script> inline). Un script embarqué ne s'exécute
// donc jamais. Le content script, lui, tourne et a accès au DOM de l'iframe via
// `allow-same-origin` : il fait la mise en page de l'extérieur. Manipuler le DOM
// (ajouter des div, poser des styles) n'est PAS bloqué par la CSP.
export function paginatePreview(doc: Document, marginMm: number = PAGE_MARGIN_MM): void {
  const win = doc.defaultView
  const resume = doc.querySelector('.resume') as HTMLElement | null
  if (!win || !resume) return
  const stage = resume.parentElement as HTMLElement | null

  const MM = 96 / 25.4
  // La marge du modèle (Compact la resserre), sinon celle passée par l'appelant.
  const MARGIN = (Number(resume.dataset.marge) || marginMm) * MM // marge de page (px)
  const PAGE = 297 * MM               // hauteur d'une feuille A4 (px)
  const SHEET_W = 210 * MM            // largeur d'une feuille A4 (px)
  const CONTENT = PAGE - 2 * MARGIN   // hauteur utile de contenu par page (px)
  const GAP = 16                      // espace gris entre deux feuilles (px)
  const STAGE_PAD = 24                // marge autour de la pile de feuilles (px)

  // Reset : enlève feuilles + décalages d'un éventuel passage précédent.
  resume.querySelectorAll('.pb-pushed').forEach((el) => {
    el.classList.remove('pb-pushed')
    ;(el as HTMLElement).style.removeProperty('--pb-gap')
  })
  resume.querySelectorAll('.pv-sheet').forEach((s) => s.remove())

  const anchorOf = (unit: HTMLElement): HTMLElement => {
    const prev = unit.previousElementSibling as HTMLElement | null
    return prev && prev.classList.contains('section-title') ? prev : unit
  }
  const ENTREES = '.exp-item, .proj-item, .edu-item'
  const enfants = (el: Element) => (Array.from(el.children) as HTMLElement[]).filter((k) => !k.classList.contains('pv-sheet'))

  // Blocs « insécables » à plat : une section avec entrées (Expériences, Formation,
  // Projets) est éclatée en [titre, entrée…] ; les petites sections (résumé,
  // compétences, langues) et l'en-tête restent d'un bloc.
  const aPlat = (conteneur: Element): HTMLElement[] =>
    enfants(conteneur).flatMap((k) =>
      !k.matches(ENTREES) && k.querySelector(ENTREES) ? aPlat(k) : [k]
    )

  // 1) Découpe le flux en pages (offsetTop inclut le padding -> la 1re page a déjà
  //    sa marge haute). Chaque bloc qui déborde est décalé en haut de la page suivante.
  type Etat = { pageIndex: number; pageBottom: number }
  const paginer = (units: HTMLElement[], depart: Etat): Etat => {
    let { pageIndex, pageBottom } = depart
    for (const unit of units) {
      const anchor = anchorOf(unit)
      const top = anchor.offsetTop
      const bottom = unit.offsetTop + unit.offsetHeight
      if (bottom > pageBottom) {
        pageIndex++
        const newTop = pageIndex * (PAGE + GAP) + MARGIN
        const spacer = Math.max(0, newTop - top)
        anchor.style.setProperty('--pb-gap', spacer + 'px')
        anchor.classList.add('pb-pushed')
        pageBottom = newTop + CONTENT
      }
    }
    return { pageIndex, pageBottom }
  }
  // « Deux colonnes » (issue #235) : chaque colonne se pagine pour elle-même,
  // depuis le même point ; la plus longue fixe le nombre de pages.
  let etat: Etat = { pageIndex: 0, pageBottom: MARGIN + CONTENT }
  for (const k of enfants(resume)) {
    if (k.classList.contains('cols')) {
      const fins = enfants(k).map((col) => paginer(aPlat(col), etat))
      etat = fins.reduce((a, b) => (b.pageIndex > a.pageIndex ? b : a), etat)
    } else {
      etat = paginer(!k.matches(ENTREES) && k.querySelector(ENTREES) ? aPlat(k) : [k], etat)
    }
  }
  const pageIndex = etat.pageIndex

  // 2) Dessine une feuille blanche A4 par page, DERRIÈRE le contenu.
  const pageCount = pageIndex + 1
  const totalH = pageIndex * (PAGE + GAP) + PAGE
  for (let p = 0; p < pageCount; p++) {
    const sheet = doc.createElement('div')
    sheet.className = 'pv-sheet'
    sheet.style.top = p * (PAGE + GAP) + 'px'
    resume.insertBefore(sheet, resume.firstChild)
  }
  resume.style.setProperty('--pv-minh', totalH + 'px')

  // 3) Met à l'échelle pour tenir dans la largeur de l'iframe.
  const avail = (stage?.parentElement?.clientWidth ?? SHEET_W) - 2 * STAGE_PAD
  const scale = Math.min(1, avail / SHEET_W)
  resume.style.setProperty('--pv-scale', String(scale))
  if (stage) {
    stage.style.width = Math.ceil(SHEET_W * scale) + 'px'
    stage.style.height = Math.ceil(totalH * scale) + 'px'
  }
}

// --- Ma mise en page (issue #235) ------------------------------------------

/** Titres en plus de `SECTION_TITLES` que le modèle de « Ma mise en page » peut
 *  poser : il dessine des blocs que le rendu standard n'a pas. */
const TITRES_PERSO: Record<ResumeLocale, { profil: string; contact: string }> = {
  fr: { profil: 'Profil', contact: 'Contact' },
  en: { profil: 'Profile', contact: 'Contact' },
  nl: { profil: 'Profiel', contact: 'Contact' },
  de: { profil: 'Profil', contact: 'Kontakt' },
}

/**
 * Remplit le modèle de « Ma mise en page » avec le CV. `null` si le modèle ne
 * passe pas `validerMiseEnPage` : l'appelant retombe sur Moderne.
 *
 * Le CV n'y est jamais éditable : un modèle écrit par l'IA ne porte pas les
 * marques `data-field` que la récolte relit, et une retouche s'y perdrait sans
 * un mot. Les sections masquées dans le panneau y sont vidées, comme ailleurs.
 */
function rendreMiseEnPage(modele: string, cv: ResumeProfile, cfg: ResolvedSettings): string | null {
  if (validerMiseEnPage(modele).length > 0) return null
  const t = SECTION_TITLES[cfg.locale]
  const sec = cfg.sections
  const contexte = {
    basics: {
      ...cv.basics,
      summary: sec.summary ? cv.basics.summary : '',
      // Seuls les liens que le rendu standard accepte (http, https).
      profiles: cv.basics.profiles
        .map((p) => ({ network: p.network, url: toResumeLink(p.url)?.href ?? '' }))
        .filter((p) => p.url),
    },
    work: sortWorkRecentFirst(cv.work),
    education: sec.education ? cv.education : [],
    skills: sec.skills ? cv.skills : [],
    languages: sec.languages ? cv.languages : [],
    projects: sec.projects ? cv.projects : [],
    photo: cfg.showPhoto && cfg.photoDataUrl ? cfg.photoDataUrl : '',
    titres: {
      ...TITRES_PERSO[cfg.locale],
      experience: t.work,
      formation: t.education,
      competences: t.skills,
      langues: t.languages,
      projets: t.projects,
      present: ONGOING_LABEL[cfg.locale],
    },
  }
  try {
    const rempli = remplirModele(modele, contexte)
    return encadrerMiseEnPage(rempli, documentBaseName({ candidate: cv.basics.name, company: cfg.company }, 'cv'), cfg.locale)
  } catch {
    return null
  }
}

// --- Render ---------------------------------------------------------------

/**
 * Rend le profil en HTML complet (avec <!doctype>, <head>, <style>) prêt à
 * insérer dans une iframe (`iframe.srcdoc = html`) ou à passer à un moteur
 * PDF (jsPDF.html, html2canvas, etc.).
 *
 * Ordre des sections **figé** pour rester ATS-friendly : contact → résumé →
 * compétences → expérience → formation → langues → projets.
 */
/** Le corps du CV, dans l'ordre ATS ; « Deux colonnes » range compétences,
 *  langues et formation dans la colonne de côté. */
function renderBody(cv: ResumeProfile, cfg: ResolvedSettings): string {
  const titles = SECTION_TITLES[cfg.locale]
  const summary = cfg.sections.summary ? renderSummary(cv, cfg) : ''
  const skills = cfg.sections.skills ? renderSkills(cv, titles, cfg) : ''
  const work = renderWork(cv, titles, cfg)
  const education = cfg.sections.education ? renderEducation(cv, titles, cfg) : ''
  const languages = cfg.sections.languages ? renderLanguages(cv, titles, cfg) : ''
  const projects = cfg.sections.projects ? renderProjects(cv, titles, cfg) : ''
  const join = (parts: string[]) => parts.filter(Boolean).join('\n')
  if (cfg.template === 'Colonnes') {
    return join([
      renderHeader(cv, cfg),
      `<div class="cols"><aside class="col-cote">${join([skills, languages, education])}</aside><div class="col-corps">${join([summary, work, projects])}</div></div>`,
    ])
  }
  return join([renderHeader(cv, cfg), summary, skills, work, education, languages, projects])
}

/** Les attributs du `.resume` propres au modèle. Rien pour ATS et Moderne :
 *  leur HTML reste identique au caractère près à celui d'avant le catalogue. */
function resumeAttrs(cfg: ResolvedSettings): string {
  const cls: Partial<Record<ResumeTemplate, string>> = { Dates: 't-dates', Colonnes: 't-colonnes', Compact: 't-compact' }
  const c = cls[cfg.template]
  const marge = margeDe(cfg.template)
  return `class="resume${c ? ` ${c}` : ''}"${marge !== PAGE_MARGIN_MM ? ` data-marge="${marge}"` : ''}`
}

export function renderResumeToHTML(cv: ResumeProfile, settings?: RenderSettings | unknown): string {
  const cfg = resolveRenderSettings(settings)
  if (cfg.template === 'Perso') {
    const perso = cfg.miseEnPage ? rendreMiseEnPage(cfg.miseEnPage, cv, cfg) : null
    if (perso !== null) return perso
    // Modèle absent ou refusé : Moderne, à la même couleur.
    cfg.template = 'Moderne'
  }
  const css = buildCss(cfg)
  const body = renderBody(cv, cfg)

  const editAttr = cfg.editable ? ' contenteditable="true" spellcheck="false"' : ''
  // Bridge de sélection -> retouches IA ad-hoc (Phase 5). N'est embarqué que
  // si la preview est éditable : aucun usage en lecture seule (export).
  const revisionScript = cfg.editable ? `<script>${buildRevisionBridgeScript()}</script>` : ''
  return `<!doctype html>
<html lang="${cfg.locale}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(documentBaseName({ candidate: cv.basics.name, company: cfg.company }, 'cv'))}</title>
<style>${css}</style>
${revisionScript}
</head>
<body>
${cfg.editable ? '<div class="pv-stage">' : ''}
<div ${resumeAttrs(cfg)}${editAttr}>
${body}
</div>
${cfg.editable ? '</div>' : ''}
</body>
</html>`
}

/**
 * Variante : rend juste le **fragment** (le `<div class="resume">…`) sans
 * doctype/head, pour insertion dans un document existant (ex : composant
 * React de prévisualisation qui contrôle déjà son propre `<head>`).
 *
 * On ré-injecte le CSS dans une `<style>` interne pour que la mise en page
 * reste autoporteuse même imbriquée.
 */
export function renderResumeFragment(cv: ResumeProfile, settings?: RenderSettings | unknown): string {
  const cfg = resolveRenderSettings(settings)
  if (cfg.template === 'Perso') cfg.template = 'Moderne'
  const css = buildCss(cfg)
  const body = renderBody(cv, cfg)
  const editAttr = cfg.editable ? ' contenteditable="true" spellcheck="false"' : ''
  return `<style>${css}</style><div ${resumeAttrs(cfg)}${editAttr}>${body}</div>`
}
